import { Component, Input, OnInit } from "@angular/core";
import { Observable, combineLatest, map, of, startWith } from "rxjs";
import { OperatorService } from "source/app/configuration/services/operator.service";
import { RideOpsUiService } from "source/app/configuration/services/rideops-ui.service";
import { LocationStateQuery } from "source/app/configuration/state/location-state.query";
import { ScannerModalService } from "source/app/modals/scanner/scanner-modal.service";
import { AlertController, ModalController } from "@ionic/angular";
import { FormControl } from "@angular/forms";
import { TranslocoService } from "@ngneat/transloco";

@Component({
	selector: "app-user-picker",
	templateUrl: "./user-picker.component.html",
	styleUrls: ["./user-picker.component.scss"],
})
export class UserPickerComponent implements OnInit {
	constructor(
		private modalController: ModalController,
		private operatorService: OperatorService,
		private locationStateQuery: LocationStateQuery,
		private scannerModalService: ScannerModalService,
		private alertController: AlertController,
		private translocoService: TranslocoService,
		public rideOpsUi: RideOpsUiService,
	) {}

	@Input() label: string;
	@Input() requiredCompetencies: DocumentReference[] = [];
	@Input() operators: Operator[] = [];
	@Input() currentOperatorId: string | undefined;

	userSearch = new FormControl("");
	searchValue$ = this.userSearch.valueChanges.pipe(
		startWith(""),
		map((x) => x.toLocaleLowerCase()),
	);

	barcodeSignIn$ = this.locationStateQuery.barcodeSignIn$;

	trackByOperatorId = (_index: number, operator: Operator) => operator.id;

	operatorsWithCompetencyStatus = this.operatorService.populateMissingCompetencies(
		this.operators,
		this.requiredCompetencies,
	);

	operators$: Observable<Operator[]>;

	select = async (operator: Operator) => {
		this.modalController.dismiss(operator);
	};

	dismiss = () => {
		this.modalController.dismiss();
	};

	clearSearch = () => {
		this.userSearch.setValue("");
	};

	scanBadge = async () => {
		const scannerModal = await this.scannerModalService.create();
		await scannerModal.present();

		const result = await scannerModal.onDidDismiss<string>();
		const code = result.data;
		if (typeof code !== "string" || code.length === 0) {
			return;
		}

		const operators = await this.operators$.firstAsync();
		const matchedOperator = operators.find((operator) => operator.externalId === code);
		if (matchedOperator === undefined) {
			const alert = await this.alertController.create({
				header: this.translocoService.translate("OPERATORS_PAGE__SCAN_NO_MATCH_TITLE"),
				message: this.translocoService.translate("OPERATORS_PAGE__SCAN_NO_MATCH_MESSAGE", { code }),
				buttons: [this.translocoService.translate("OPERATOR_LOGIN__OK")],
				cssClass: this.rideOpsUi.isV2Active() ? "operators-v2-dialog" : undefined,
			});
			await alert.present();
			return;
		}

		await this.select(matchedOperator);
	};

	private setup = () => {
		this.operatorsWithCompetencyStatus = this.operatorService.populateMissingCompetencies(
			this.operators,
			this.requiredCompetencies,
		);

		this.operators$ = combineLatest([of(this.operatorsWithCompetencyStatus), this.searchValue$]).pipe(
			map(([operators, searchValue]) => {
				const sortByCurrentOperatorAndName = (a: Operator, b: Operator): number => {
					const aIsOperator = a.id == this.currentOperatorId ? 0 : 1;
					const bIsOperator = b.id == this.currentOperatorId ? 0 : 1;

					return aIsOperator - bIsOperator || a.name.localeCompare(b.name);
				};

				const filteredOperators =
					searchValue === ""
						? operators
						: operators.filter((x) => x.name.toLocaleLowerCase().indexOf(searchValue) > -1);
				return [...filteredOperators].sort(sortByCurrentOperatorAndName);
			}),
		);
	};

	ngOnInit(): void {
		this.setup();
	}
}
